/**
 * @healthdesk/shared-services compatibility barrel (CareStart3 port).
 *
 * Maps the experimental monorepo package to this repo's real infrastructure:
 * - firebaseApp / db / storage / functions / FUNCTIONS_REGION: this repo's live
 *   Firebase (services/firebase)
 * - ddx helpers (getAllDiagnoses, mergeDiagnosisArrays, ...): ported pure utilities
 * - constants (specialties, languages, conversation templates): ported data modules
 *
 * NOTE: The symbols in the STUBS block are imported only by over-copied symptom-checker
 * / dashboard files that are NOT in the CareStart3 reachable graph. They are provided as
 * throwing stubs so those modules resolve; if a future feature routes through them, port
 * the real service from packages/shared-services.
 */

import firebaseApp, { db, storage } from "services/firebase";

export const FUNCTIONS_REGION =
  process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION || "us-central1";

const functions = firebaseApp.functions();

export { firebaseApp, db, storage, functions };

export {
  getAllDiagnoses,
  extractDiagnosisName,
  normalizeConditionKey,
  mergeDiagnosisArrays,
} from "./ddxUtils";

export * from "./constants";

// DDX-Plus Firestore reader/writer layer (Phase 2 Task 2). Ported from Exp-B's
// packages/shared-services/src/services/ddxService.js to replace the throwing
// stubs below. Exposes getChiefComplaintDocument, getShiftFactors, the grounding
// readers (getShiftEdgesForFactors, getKnownFactorIds, getShiftConditionDdxMap,
// createDdxKbProjectionDeps, fetchDdxCalculatorsCatalog, writeDdxAuditJob,
// callDdxAuthoritativeSourceConsult, callDdxKbVerificationEnqueue) plus the full
// KB admin surface (conditions/factors/edges/factor-queue CRUD, benchmarks).
export * from "./ddxService";

const notPorted = (name) => (...args) => {
  void args;
  throw new Error(
    `[CareStart3 port] ${name}() from @healthdesk/shared-services is not ported in this repo yet.`,
  );
};

// --- STUBS (not in the CareStart3 reachable graph; port on demand) ---
export const getCachedProfile = notPorted("getCachedProfile");
export const setCachedProfile = notPorted("setCachedProfile");
export const formatFactorsForPatient = notPorted("formatFactorsForPatient");
export const extractDscrClarifyingStructuredItems = notPorted(
  "extractDscrClarifyingStructuredItems",
);
export const updatePatientCMR = notPorted("updatePatientCMR");
export const createCheckInVisit = notPorted("createCheckInVisit");
export const queueEmailNotification = notPorted("queueEmailNotification");
export const submitEdLocationClaimChallenge = notPorted(
  "submitEdLocationClaimChallenge",
);
export const submitEdLocationDataFlag = notPorted("submitEdLocationDataFlag");
export const getCheckInTemplates = notPorted("getCheckInTemplates");
export const createCheckInTemplate = notPorted("createCheckInTemplate");
export const deleteCheckInTemplate = notPorted("deleteCheckInTemplate");
export const updateCheckInTemplate = notPorted("updateCheckInTemplate");
export const generateCheckInQR = notPorted("generateCheckInQR");
export const generateFieldId = notPorted("generateFieldId");
export const reorderCheckInFields = notPorted("reorderCheckInFields");
export const setDefaultTemplate = notPorted("setDefaultTemplate");
export const getConversationTemplates = notPorted("getConversationTemplates");
export const createConversationTemplate = notPorted(
  "createConversationTemplate",
);
export const updateConversationTemplate = notPorted(
  "updateConversationTemplate",
);
export const deleteConversationTemplate = notPorted(
  "deleteConversationTemplate",
);
